/**
 * DebtScopeDisclosure — "See what's included" for the debt plan.
 *
 * Every debt counted, and every debt NOT counted with the reason. This is the
 * component that makes an omission impossible to miss.
 */
import React from 'react';
import { Check, MinusCircle } from 'lucide-react';
import { formatMoney } from '@/lib/format';
import { longMonth } from '@/lib/debtPlanFormat';

export function DebtScopeDisclosure({ scope }) {
  const included = scope?.included || [];
  const excluded = (scope?.excluded || []).filter(
    (e) => e.reason === 'no_payment_recorded' || e.reason === 'payment_below_interest');
  const cleared = (scope?.excluded || []).filter(
    (e) => e.reason === 'cleared' || e.reason === 'closed');

  return (
    <div className="mt-4 grid gap-4 md:grid-cols-2" data-testid="debt-plan-disclosure">
      <div>
        <p className="text-[10px] uppercase tracking-widest briefing-hero__accent font-medium mb-2">
          In this plan ({included.length})
        </p>
        <ul className="space-y-2">
          {included.map((r) => (
            <li key={r.id} className="flex items-start gap-2 text-xs" data-testid={`debt-included-${r.id}`}>
              <Check className="h-3.5 w-3.5 mt-0.5 shrink-0 briefing-hero__accent" aria-hidden="true" />
              <span className="min-w-0">
                <span className="font-medium">{r.name}</span>
                <span className="briefing-hero__muted">
                  {' · '}{formatMoney(r.balance, r.currency, { compact: true })}
                  {r.apr ? ` · ${r.apr}%` : ' · no rate on record'}
                  {r.payoff_date ? ` · clear by ${longMonth(r.payoff_date)}` : ''}
                </span>
              </span>
            </li>
          ))}
          {included.length === 0 && (
            <li className="text-xs briefing-hero__muted">Nothing can be projected yet.</li>
          )}
        </ul>
      </div>

      <div>
        <p className="text-[10px] uppercase tracking-widest briefing-hero__accent font-medium mb-2">
          Not in this plan ({excluded.length + cleared.length})
        </p>
        <ul className="space-y-2">
          {excluded.map((r) => (
            <li key={r.id} className="flex items-start gap-2 text-xs" data-testid={`debt-excluded-${r.id}`}>
              <MinusCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 briefing-hero__muted" aria-hidden="true" />
              <span className="min-w-0">
                <span className="font-medium">{r.name}</span>
                <span className="briefing-hero__muted">
                  {' · '}{formatMoney(r.balance, r.currency, { compact: true })}
                  {' — '}{r.reason_label}
                </span>
              </span>
            </li>
          ))}
          {cleared.map((r) => (
            <li key={r.id} className="flex items-start gap-2 text-xs briefing-hero__muted">
              <MinusCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
              <span>{r.name} — {r.reason_label}</span>
            </li>
          ))}
          {excluded.length + cleared.length === 0 && (
            <li className="text-xs briefing-hero__muted">Everything you owe is counted.</li>
          )}
        </ul>
      </div>
    </div>
  );
}

export default DebtScopeDisclosure;
